import React, { useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, Form, Input, Row, Select, Space, Typography, message,
} from 'antd';
import { ArrowLeftOutlined, BulbOutlined, SendOutlined } from '@ant-design/icons';
import * as api from '../api/client';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Props {
  onCreated: () => void;
  onCancel?: () => void;
}

const CATEGORIES = [
  'AI_AGENTS', 'PLAYBOOKS', 'KNOWLEDGE_BASE', 'API', 'INTEGRATIONS',
  'DESKTOP_APP', 'UI_UX', 'ADMIN', 'SECURITY', 'BILLING', 'OTHER',
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  AI_AGENTS: 'AI Agents', PLAYBOOKS: 'Playbooks', KNOWLEDGE_BASE: 'Knowledge Base',
  API: 'API', INTEGRATIONS: 'Integrations', DESKTOP_APP: 'Desktop App',
  UI_UX: 'UI / UX', ADMIN: 'Admin', SECURITY: 'Security', BILLING: 'Billing', OTHER: 'Other',
};

const IMPACT_LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
const VISIBILITIES = ['PUBLIC', 'PRIVATE_ENTERPRISE', 'INTERNAL_ONLY'] as const;

const VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC: 'Public', PRIVATE_ENTERPRISE: 'Private (Enterprise)', INTERNAL_ONLY: 'Internal Only',
};

const FeatureRequestForm: React.FC<Props> = ({ onCreated, onCancel }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [problemStatement, setProblemStatement] = useState('');
  const [proposedSolution, setProposedSolution] = useState('');
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('OTHER');
  const [impactLevel, setImpactLevel] = useState<(typeof IMPACT_LEVELS)[number]>('MEDIUM');
  const [visibility, setVisibility] = useState<(typeof VISIBILITIES)[number]>('PUBLIC');
  const [submitting, setSubmitting] = useState(false);
  const [improving, setImproving] = useState(false);
  const [possibleDuplicates, setPossibleDuplicates] = useState<Array<{ id: string; title: string; similarity_score: number }>>([]);

  const canSubmit = useMemo(() => {
    return title.trim().length >= 5 && (description.trim().length > 0 || problemStatement.trim().length > 0);
  }, [title, description, problemStatement]);

  const handleImproveWithAI = async () => {
    if (!title.trim() && !description.trim() && !problemStatement.trim()) {
      message.warning('Add some draft text first.');
      return;
    }
    setImproving(true);
    try {
      const prompt = [
        'Improve this feature request.',
        `Title: ${title}`,
        `Description: ${description}`,
        `Problem statement: ${problemStatement}`,
        `Proposed solution: ${proposedSolution}`,
        'Return JSON with keys: title, description, suggestedCategory.',
      ].join('\n');
      const res = await api.chatWithLLM([{ role: 'user', content: prompt }]);
      const content = res?.data?.message || res?.data?.reply || '';
      const jsonStart = String(content).indexOf('{');
      const jsonEnd = String(content).lastIndexOf('}');
      if (jsonStart >= 0 && jsonEnd > jsonStart) {
        const parsed = JSON.parse(String(content).slice(jsonStart, jsonEnd + 1));
        if (parsed.title) setTitle(String(parsed.title));
        if (parsed.description) setDescription(String(parsed.description));
        if (parsed.suggestedCategory && CATEGORIES.includes(parsed.suggestedCategory)) {
          setCategory(parsed.suggestedCategory);
        }
        message.success('Draft improved with AI suggestions.');
      } else {
        message.info('AI returned plain text; no structured changes applied.');
      }
    } catch {
      message.warning('AI improvement unavailable right now. You can still submit.');
    } finally {
      setImproving(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      message.error('Please complete required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.createFeatureRequest({
        title: title.trim(),
        description: description.trim(),
        problemStatement: problemStatement.trim(),
        proposedSolution: proposedSolution.trim(),
        category,
        impactLevel,
        visibility,
      });
      setPossibleDuplicates(res.data?.possibleDuplicates || []);
      message.success('Your feature request has been submitted for admin review. You can monitor its status under My Requests.');
      setTitle('');
      setDescription('');
      setProblemStatement('');
      setProposedSolution('');
      setCategory('OTHER');
      setImpactLevel('MEDIUM');
      setVisibility('PUBLIC');
      onCreated();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to submit feature request.');
    } finally {
      setSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (title.trim().length < 5) {
      setPossibleDuplicates([]);
      return;
    }
    const timeout = window.setTimeout(async () => {
      try {
        const res = await api.getFeatureRequests({ search: title.trim(), sort: 'mostVoted' });
        const matches = (res.data.items || []).slice(0, 5).map(i => ({
          id: i.id,
          title: i.title,
          similarity_score: i.title.toLowerCase().includes(title.trim().toLowerCase()) ? 0.8 : 0.4,
        }));
        setPossibleDuplicates(matches);
      } catch {
        // best effort
      }
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [title]);

  return (
    <div style={{ width: '100%' }}>
      {onCancel && (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onCancel}
          style={{ marginBottom: 16 }}
        >
          Back
        </Button>
      )}

      <Card
        className="kb-card praxys-feature-form-card"
        title={
          <div>
            <Title level={4} style={{ margin: 0, color: '#e6edf3' }}>Submit Feature Request</Title>
            <Text style={{ fontWeight: 400, fontSize: 13, color: '#e6edf3' }}>
              Requests require admin approval before community voting.
            </Text>
          </div>
        }
        style={{ width: '100%' }}
      >
        <Text className="praxys-feature-form-note">
          Your request will be reviewed by an admin before it becomes visible for community voting.
        </Text>

        <Row gutter={[24, 0]}>
          {/* Left column: text inputs */}
          <Col xs={24} lg={16}>
            <Form layout="vertical">
              <Form.Item
                label={<span style={{ color: '#e6edf3' }}>Title</span>}
                required
                extra={<span style={{ color: '#8b949e' }}>Keep it short and specific.</span>}
                validateStatus={title.trim().length > 0 && title.trim().length < 5 ? 'error' : ''}
                help={title.trim().length > 0 && title.trim().length < 5 ? 'Title must be at least 5 characters.' : undefined}
              >
                <Input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  maxLength={150}
                  placeholder="e.g. Add bulk export to CSV from the task list"
                  showCount
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ color: '#e6edf3' }}>Problem Statement</span>}
                extra={<span style={{ color: '#8b949e' }}>What problem are you trying to solve?</span>}
              >
                <TextArea
                  value={problemStatement}
                  onChange={e => setProblemStatement(e.target.value)}
                  rows={3}
                  placeholder="Describe the problem or pain point."
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ color: '#e6edf3' }}>Description</span>}
                required={description.trim().length === 0 && problemStatement.trim().length === 0}
                extra={<span style={{ color: '#8b949e' }}>Provide context and background for your request.</span>}
              >
                <TextArea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={4}
                  placeholder="Describe the feature you'd like to see."
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ color: '#e6edf3' }}>Proposed Solution</span>}
                extra={<span style={{ color: '#8b949e' }}>Optional: describe how you imagine this working.</span>}
              >
                <TextArea
                  value={proposedSolution}
                  onChange={e => setProposedSolution(e.target.value)}
                  rows={3}
                  placeholder="Optionally describe your proposed implementation."
                />
              </Form.Item>

              {possibleDuplicates.length > 0 && (
                <Form.Item>
                  <Alert
                    type="warning"
                    showIcon
                    message="Possible similar requests — please check before submitting"
                    description={
                      <ul style={{ margin: '8px 0 0', paddingLeft: 16 }}>
                        {possibleDuplicates.map(d => (
                          <li key={d.id} style={{ marginBottom: 4 }}>
                            <Text style={{ color: '#e6edf3' }}>{d.title}</Text>
                            <Text style={{ marginLeft: 8, color: '#8b949e' }}>
                              {Math.round((d.similarity_score || 0) * 100)}% match
                            </Text>
                          </li>
                        ))}
                      </ul>
                    }
                  />
                </Form.Item>
              )}
            </Form>
          </Col>

          {/* Right column: dropdowns + action buttons */}
          <Col xs={24} lg={8}>
            <Form layout="vertical">
              <Form.Item label={<span style={{ color: '#e6edf3' }}>Category</span>}>
                <Select
                  value={category}
                  onChange={v => setCategory(v)}
                  style={{ width: '100%' }}
                  options={CATEGORIES.map(c => ({ value: c, label: CATEGORY_LABELS[c] || c }))}
                />
              </Form.Item>

              <Form.Item label={<span style={{ color: '#e6edf3' }}>Impact Level</span>}>
                <Select
                  value={impactLevel}
                  onChange={v => setImpactLevel(v)}
                  style={{ width: '100%' }}
                  options={IMPACT_LEVELS.map(i => ({ value: i, label: i }))}
                />
              </Form.Item>

              <Form.Item label={<span style={{ color: '#e6edf3' }}>Visibility</span>}>
                <Select
                  value={visibility}
                  onChange={v => setVisibility(v)}
                  style={{ width: '100%' }}
                  options={VISIBILITIES.map(v => ({ value: v, label: VISIBILITY_LABELS[v] || v }))}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    icon={<BulbOutlined />}
                    loading={improving}
                    onClick={handleImproveWithAI}
                    style={{ width: '100%' }}
                  >
                    Improve with AI
                  </Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={submitting}
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                    style={{ width: '100%' }}
                  >
                    Submit for Review
                  </Button>
                  {onCancel && (
                    <Button onClick={onCancel} style={{ width: '100%' }}>Cancel</Button>
                  )}
                </Space>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default FeatureRequestForm;
